import { useId, useRef, useState } from 'react';
import { DPI, MIN_DPI_WARN } from '../lib/constants';
import { downsampleToPanel, isAcceptedImage, recropSlot, revokeSlotImage } from '../lib/canvas';
import { effectiveCard } from '../lib/geometry';
import { CropModal } from './CropModal';
import type { CardSize, Orientation, SlotId, SlotImage, SourceRotation } from '../types';

interface Props {
  slotId: SlotId;
  label: string;
  required?: boolean;
  image: SlotImage | undefined;
  cardSize: CardSize;
  orientation: Orientation;
  preserveResolution: boolean;
  onChange: (slotId: SlotId, next: SlotImage | undefined) => void;
}

export function ImageSlot({
  slotId,
  label,
  required,
  image,
  cardSize,
  orientation,
  preserveResolution,
  onChange,
}: Props) {
  const inputId = useId();
  const fileInput = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cropOpen, setCropOpen] = useState(false);

  const effC = effectiveCard(cardSize, orientation);

  const handleCropSave = async (transforms: {
    cropX: number;
    cropY: number;
    rotation: SourceRotation;
    flipH: boolean;
    flipV: boolean;
  }) => {
    if (!image) return;
    setCropOpen(false);
    setError(null);
    setBusy(true);
    try {
      const next = await recropSlot(image, effC.widthPx, effC.heightPx, {
        ...transforms,
        preserveResolution,
      });
      revokeSlotImage(image);
      onChange(slotId, next);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const ingest = async (file: File) => {
    setError(null);
    if (!isAcceptedImage(file)) {
      setError('Use JPG, PNG, or WebP. (Convert HEIC to JPG first.)');
      return;
    }
    setBusy(true);
    try {
      const next = await downsampleToPanel(file, effC.widthPx, effC.heightPx, {
        preserveResolution,
      });
      revokeSlotImage(image);
      onChange(slotId, next);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleFile = (file: File | undefined) => {
    if (file) void ingest(file);
  };

  const handleClear = () => {
    revokeSlotImage(image);
    onChange(slotId, undefined);
    if (fileInput.current) fileInput.current.value = '';
  };

  const sourceDpi = image
    ? Math.round(Math.min(image.width / effC.widthIn, image.height / effC.heightIn))
    : null;
  const lowDpi = sourceDpi !== null && sourceDpi < MIN_DPI_WARN;

  return (
    <div className={`slot${dragOver ? ' slot-drop' : ''}${image ? ' slot-filled' : ''}`}>
      <label htmlFor={inputId} className="slot-header">
        <span className="slot-label">{label}</span>
        <span className="slot-required">{required ? 'required' : 'optional'}</span>
      </label>

      <div
        className="slot-body"
        style={{ aspectRatio: `${effC.widthIn} / ${effC.heightIn}` }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleFile(e.dataTransfer.files?.[0]);
        }}
        onClick={() => fileInput.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') fileInput.current?.click();
        }}
      >
        {image ? (
          <img src={image.previewUrl} alt={`${label} preview`} className="slot-preview" />
        ) : (
          <div className="slot-empty">
            <span className="slot-plus">+</span>
            <span>Drop image or click</span>
            <span className="slot-hint">
              Suggested {Math.round(effC.widthIn * DPI)}×{Math.round(effC.heightIn * DPI)} px
            </span>
          </div>
        )}
        {busy && <div className="slot-busy">Processing…</div>}
      </div>

      <input
        ref={fileInput}
        id={inputId}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="visually-hidden"
        onChange={(e) => handleFile(e.target.files?.[0] ?? undefined)}
      />

      <div className="slot-footer">
        {image ? (
          <>
            <span className="slot-meta">
              source {image.width}×{image.height}
              {sourceDpi !== null && ` · ~${sourceDpi} DPI`}
            </span>
            <span className="slot-footer-actions">
              <button type="button" className="link-btn" onClick={() => setCropOpen(true)}>
                Edit image
              </button>
              <button type="button" className="link-btn" onClick={handleClear}>
                Clear
              </button>
            </span>
          </>
        ) : (
          <span className="slot-meta">&nbsp;</span>
        )}
      </div>

      {lowDpi && (
        <p className="slot-warn">
          Source is below ~{MIN_DPI_WARN} DPI for {cardSize.label}. Print may look soft.
        </p>
      )}
      {error && <p className="slot-error">{error}</p>}

      {cropOpen && image && (
        <CropModal
          source={image.source}
          sourceWidth={image.width}
          sourceHeight={image.height}
          panelWidth={effC.widthPx}
          panelHeight={effC.heightPx}
          initialCropX={image.cropX}
          initialCropY={image.cropY}
          initialRotation={image.rotation}
          initialFlipH={image.flipH}
          initialFlipV={image.flipV}
          label={label}
          onSave={handleCropSave}
          onCancel={() => setCropOpen(false)}
        />
      )}
    </div>
  );
}
